import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Feather, Sun, Moon, LogOut, User as UserIcon, ChevronLeft, ShieldCheck, LifeBuoy, HeartHandshake, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import SupportCenter from "@/components/SupportCenter";
import DonateDialog from "@/components/DonateDialog";
import { useTour } from "@/context/TourContext";

export default function Header({ back, children }) {
  const { user, logout, updateProfile } = useAuth();
  const { startTour } = useTour();
  const { theme, toggle } = useTheme();
  const [profileOpen, setProfileOpen] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", pen_name: user?.pen_name || "" });
  const [supportOpen, setSupportOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const displayName = user?.pen_name || user?.name || "Writer";
  const initials = displayName.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const check = async () => {
      try { const { data } = await api.get("/support/unread-count"); if (alive) setUnread(data.count || 0); }
      catch { /* ignore */ }
    };
    check();
    const t = setInterval(check, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  const saveProfile = async () => {
    await updateProfile(form);
    setProfileOpen(false);
    toast.success("Profile updated");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="flex items-center gap-4 px-4 sm:px-6 min-h-[4rem] py-2">
        {back ? (
          <Link to="/app" data-testid="header-back-link" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Projects</span>
          </Link>
        ) : (
          <Link to="/app" className="flex items-center gap-2.5" data-testid="app-header-logo">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <Feather className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display text-2xl font-bold hidden sm:block">CodexStudio</span>
          </Link>
        )}

        <div className="flex-1 flex items-center min-w-0">{children}</div>

        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="shrink-0">
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="shrink-0 relative">
              <Avatar className="w-9 h-9 border border-border">
                <AvatarFallback className="bg-primary/15 text-primary font-semibold text-sm">{initials}</AvatarFallback>
              </Avatar>
              {unread > 0 && (
                <span data-testid="support-unread-dot" className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-primary ring-2 ring-background" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-popover">
            <DropdownMenuLabel>
              <div className="font-semibold">{displayName}</div>
              <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setForm({ name: user?.name || "", pen_name: user?.pen_name || "" }); setProfileOpen(true); }} data-testid="open-profile">
              <UserIcon className="w-4 h-4 mr-2" /> Profile & pen name
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => startTour()} data-testid="open-tour">
              <Compass className="w-4 h-4 mr-2" /> Take a tour
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSupportOpen(true)} data-testid="open-support">
              <LifeBuoy className="w-4 h-4 mr-2" /> Get help
              {unread > 0 && <span className="ml-auto w-2 h-2 rounded-full bg-primary" />}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDonateOpen(true)} data-testid="open-donate">
              <HeartHandshake className="w-4 h-4 mr-2" /> Support CodexStudio
            </DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem asChild data-testid="open-admin">
                <Link to="/admin"><ShieldCheck className="w-4 h-4 mr-2" /> Admin dashboard</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive focus:text-destructive">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="bg-popover">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Your identity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Pen name</Label>
              <Input value={form.pen_name} onChange={(e) => setForm({ ...form, pen_name: e.target.value })}
                placeholder="Shown publicly instead of your real name" data-testid="profile-penname-input" />
              <p className="text-xs text-muted-foreground">When set, you appear as your pen name across the studio.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveProfile} data-testid="save-profile-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupportCenter open={supportOpen} onOpenChange={setSupportOpen} onRead={() => setUnread(0)} />
      <DonateDialog open={donateOpen} onOpenChange={setDonateOpen} defaultName={user?.name || ""} defaultEmail={user?.email || ""} />
    </header>
  );
}
